/**
 * Session state service: persists critical UI state to IndexedDB so it
 * survives iOS process-kills and short eviction windows.
 *
 * Stored data:
 *  - route      last pathname + search
 *  - scrollY    vertical scroll offset
 *  - formDrafts generic key/value map for in-progress form values
 *  - timestamp  epoch ms (used for TTL check)
 *
 * TTL: entries older than 1 hour are ignored on restore.
 */

import localforage from 'localforage';

const SESSION_STORE = localforage.createInstance({
  name: 'b2b_session',
  storeName: 'ui_state',
});

const SESSION_KEY = 'ui_session_state';
const TTL_MS = 3_600_000; // 1 hour

export interface UISessionState {
  route: string;
  scrollY: number;
  formDrafts: Record<string, unknown>;
  timestamp: number;
}

export const sessionStateService = {
  async save(state: UISessionState): Promise<void> {
    await SESSION_STORE.setItem<UISessionState>(SESSION_KEY, state);
  },

  async load(): Promise<UISessionState | null> {
    const s = await SESSION_STORE.getItem<UISessionState>(SESSION_KEY);
    if (!s) return null;
    if (Date.now() - s.timestamp > TTL_MS) return null;
    return s;
  },

  async clear(): Promise<void> {
    await SESSION_STORE.removeItem(SESSION_KEY);
  },
};
